<template>
    <div>
        <tracker-new-data-header v-if="!editTarget" :columns="columns"></tracker-new-data-header>
        <div v-if="canTrackManually" class="row px-3 new-data-inputs">
            <div class="col-2 px-1 py-3">
                <v-date-picker
                    dusk="date"
                    placeholder="MM/DD/YYYY"
                    id="date"
                    class="picker-container"
                    popover-align="left"
                    v-model="date"
                ></v-date-picker>
            </div>
            <div class="col-5 px-1 py-3">
                <div class="d-flex justify-content-start align-items-center">
                    <div class="d-flex flex-column">
                        <div class="d-flex justify-content-start align-items-center ">
                            <time-input v-model="duration"></time-input>
                            <div
                                v-if="goal.parameters.track_intensity && !isNaN(durationIconset)"
                                class="ml-2"
                            >
                                <intensity
                                    :iconset="durationIconset"
                                    :mini="true"
                                    :session="true"
                                    @select-prompt="setIntensity($event)"
                                    :selected-prompt="intensity ? intensity[1] : null"
                                ></intensity>
                            </div>
                            <button
                                class="btn btn-sm btn-outline-primary ml-2"
                                :disabled="!canAddDuration()"
                                @click="addDuration()"
                            >
                                <i class="mr-1 fa fa-plus-circle"></i>
                                ADD
                            </button>
                        </div>

                        <div v-for="(r, i) in results" class="d-flex align-items-center mt-2">
                            <time-input :key="i" v-model="results[i]"></time-input>
                            <div
                                v-if="goal.parameters.track_intensity && !isNaN(durationIconset)"
                                class="ml-2"
                            >
                                <!--Creating a new value: resultsIntensity is an array of prompt arrays-->
                                <intensity
                                    :mini="true"
                                    v-if="Array.isArray(resultsIntensity[i])"
                                    :prompt="[durationIconset, resultsIntensity[i][1]]"
                                ></intensity>
                                <!--Editing value: resultsIntensity is an array of the actual values-->
                                <intensity
                                    v-else=""
                                    :mini="true"
                                    :iconset="durationIconset"
                                    :session="true"
                                    @select-prompt="updateIntensity(i, $event)"
                                    ref="intensityIconComponent"
                                    :selected-value="resultsIntensity[i]"
                                ></intensity>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="px-1 col-1 py-3">
                {{ results && results.length ? results.length : 0 }}
            </div>
            <div class="px-1 col-1 py-3">
                {{
                    results && results.length
                        ? parseInt(
                              goal.parameters.success
                                  ? (100 * Math.max(...results)) / goal.parameters.success
                                  : 0
                          )
                        : 0
                }}%
            </div>
            <div class="px-1 col-2 py-3">
                <input v-model="note" class="form-control form-control-sm" name="note" />
            </div>
            <tracker-new-data-actions
                :saving="saving"
                :edit-target="editTarget"
                @save-data="submitData()"
                @edit-done="$emit('edit-done')"
            ></tracker-new-data-actions>
        </div>
    </div>
</template>

<script>
import { trackerNewDataCommon } from "./trackerNewDataCommon";
import Intensity from "../../ui/IntensityIcon";

export default {
    name: "TrackerNewDataDuration",
    components: {
        Intensity,
    },
    mixins: [trackerNewDataCommon],
    data() {
        return {
            columns: {
                titles: [`Date`, `Duration`, `Times`, `Percent`, `Note`, ``],
                col: [2, 5, 1, 1, 3, 1],
            },
            date: new Date(),
            success: 0,
            results: [],
            /* Start Duration specific */
            intensity: null,
            resultsIntensity: [],
            durationIconset: null,
            /* End Duration specific */
            note: "",
            duration: 0,
        };
    },

    async mounted() {
        if (!this.editTarget) {
            //If we're not editing an exisiting goal, fetch the iconset from the customizations
            this.durationIconset = await this.getDurationIconset();
        }
        if (this.editTarget) {
            this.populateEdit();
        }
    },

    beforeDestroy() {
        this.resetComponent();
    },

    methods: {
        populateEdit() {
            let editTarget = { ...this.editTarget };
            this.date = editTarget.session_at;
            this.note = editTarget.notes;
            this.results = editTarget.data.results;
            this.resultsIntensity = editTarget.data.intensity;
            this.durationIconset = editTarget.data.iconset;
        },

        async getDurationIconset() {
            let customizations = await axios.get(
                `/api/users/${this.$root.user.id}/tracker_customizations?organization=${
                    this.$root.organization.id
                }`
            );
            let durationCustomization = _.get(customizations, "data.data", []).find(
                customization => customization.type === "duration"
            );
            return _.get(durationCustomization, "default_options.iconset", 0);
        },

        setIntensity(event) {
            this.intensity = event;
        },

        updateIntensity(index, event) {
            this.$set(this.resultsIntensity, index, event[0]);
        },

        canAddDuration() {
            if (this.goal.parameters.track_intensity) {
                return this.intensity;
            }
            return true;
        },

        addDuration() {
            if (!this.canAddDuration()) return;
            this.results.push(this.duration);
            if (this.goal.parameters.track_intensity) {
                this.resultsIntensity.push(this.intensity);
            }
            this.duration = 0;
            this.intensity = null;
        },

        submitData() {
            let results = [...this.results];

            if (!results.length) {
                if (this.duration) {
                    results.push(this.duration);
                } else {
                    this.saving = false;
                    return;
                }
            } else if (this.duration) {
                results.push(this.duration);
            }

            let success = 0;
            switch (this.goal.parameters.evaluate) {
                case "average":
                    success =
                        Math.round(
                            (10 * this.results.reduce((a, b) => a + b, 0)) / this.results.length
                        ) / 10;
                    break;
                case "max":
                default:
                    success = this.results.reduce((a, b) => Math.max(a, b));
            }

            let payload = {
                data: {
                    type: "duration",
                    success,
                    results,
                },
                notes: this.note,
                session_at: this.isoDate,
            };

            if (this.goal.parameters.track_intensity) {
                payload = this.addIntensityParams(payload);
            }

            this.$emit("save-data", payload);
        },

        addIntensityParams(payload) {
            let resultsIntensity = [...this.resultsIntensity];
            if (!resultsIntensity.length) {
                if (this.intensity) {
                    resultsIntensity.push(this.intensity);
                } else {
                    this.saving = false;
                    return;
                }
            } else if (this.intensity) {
                resultsIntensity.push(this.intensity);
            }
            payload.data.iconset = this.durationIconset;
            payload.data.intensity = {
                ...resultsIntensity.map(intensity =>
                    Array.isArray(intensity) ? intensity[0] : intensity
                ),
            };
            return payload;
        },

        resetComponent() {
            this.date = new Date();
            this.success = 0;
            this.results = [];
            this.intensity = null;
            this.resultsIntensity = [];
            this.durationIconset = null;
            this.note = "";
            this.duration = 0;
        },
    },
};
</script>

<style scoped></style>
